import { z } from 'zod';
import { isValidMoney } from '../money';
import { isLocalDate, isLocalTime } from '../time';

/** UUID (v7 generowane w aplikacji). */
export const idSchema = z.string().uuid({ message: 'Nieprawidłowy identyfikator' });

/** Kwota jako string z max. dwoma miejscami, np. "250.00". */
export const moneySchema = z.string().trim().refine(isValidMoney, { message: 'Nieprawidłowa kwota' });

/** Data biznesowa "YYYY-MM-DD" (Europe/Warsaw). */
export const localDateSchema = z
  .string()
  .refine(isLocalDate, { message: 'Nieprawidłowa data' })
  .describe('Data YYYY-MM-DD (Europe/Warsaw)');

/** Godzina "HH:MM". */
export const localTimeSchema = z
  .string()
  .refine(isLocalTime, { message: 'Nieprawidłowa godzina' })
  .describe('Godzina HH:MM (Europe/Warsaw)');

/** Czas w sekundach (całkowity, > 0). */
export const secondsSchema = z.number().int().positive();

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, { message: 'Adres email jest za długi' })
  .email({ message: 'Nieprawidłowy adres email' });

export const okSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof okSchema>;

/** Wersja rekordu do edycji współbieżnej (CONFLICT przy niezgodności). */
export const versionSchema = z.number().int().positive();

export const paginationInput = z.object({
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type PaginationInput = z.infer<typeof paginationInput>;

// ── Wspólne klocki modułów domenowych (Fala 1) ──────────────────────────────

/** Waluty obsługiwane w formularzach (w bazie: kod ISO 4217, CHECK `^[A-Z]{3}$`). */
export const CURRENCIES = ['PLN', 'EUR', 'USD'] as const;
export type Currency = (typeof CURRENCIES)[number];
export const currencySchema = z.enum(CURRENCIES);
export const DEFAULT_CURRENCY: Currency = 'PLN';

/** Stawka godzinowa: numeric(10,2) w bazie, string w TS ("150.00"), nieujemna. */
export const rateSchema = z
  .string()
  .trim()
  .regex(/^\d{1,8}(\.\d{1,2})?$/, { message: 'Nieprawidłowa stawka' });

/** Kwota nieujemna: numeric(12,2) w bazie, string w TS ("1990.00"). */
export const amountSchema = z
  .string()
  .trim()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, { message: 'Nieprawidłowa kwota' });

/** Kolor (dane, np. kolor klienta) w formacie #RRGGBB. */
export const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, { message: 'Nieprawidłowy kolor' });

/** Adres www (http/https). */
export const urlSchema = z
  .string()
  .trim()
  .max(2048, { message: 'Adres jest za długi' })
  .url({ message: 'Nieprawidłowy adres' })
  .refine((v) => /^https?:\/\//i.test(v), { message: 'Adres musi zaczynać się od http:// albo https://' });

/** Krótki tekst w jednej linii (nazwy, tytuły). */
export const nameSchema = z
  .string()
  .trim()
  .min(1, { message: 'Pole jest wymagane' })
  .max(200, { message: 'Najwyżej 200 znaków' });

/** Dłuższy tekst Markdown (opisy, notatki). */
export const markdownSchema = z.string().max(50_000, { message: 'Tekst jest za długi' });

/** Odnośnik do rekordu innego modułu (pochodzenie zadania, źródło wiedzy itp.). */
export const recordLinkSchema = z.object({ type: z.string().min(1).max(40), id: idSchema });
export type RecordLink = z.infer<typeof recordLinkSchema>;

/** Odcień statusu w UI (komponent Status w apps/web: kropka + etykieta). */
export const STATUS_TONES = ['neutral', 'accent', 'success', 'warning', 'danger'] as const;
export type StatusTone = (typeof STATUS_TONES)[number];
